/**
 * No pintar nada hasta que la sesión esté resuelta.
 *
 * ## El fallo que lo trajo
 *
 * Hasta que hubo enrutador, **toda** entrada pasaba por `main.tsx`, que esperaba a
 * la sesión antes de decidir qué aplicación montar. Con rutas de verdad cada URL es
 * una puerta: abrir `/cobros/recibos` en una pestaña nueva monta el marco del panel
 * directamente, y `useYo()` —que exige sesión— lanza «useYo fuera de una sesión
 * abierta». Se vio en el navegador el 17-09-2026, escribiendo una URL a mano.
 *
 * ## Por qué **no** redirige al acceso
 *
 * El primer intento mandaba a `/acceso?volverA=…` y **colgó el navegador con un
 * bucle**: la sesión pasa por más de un estado al cerrarla, y basta con que la fase
 * oscile un render para que las dos rutas se manden la una a la otra sin parar.
 *
 * Así que aquí no se navega: **se pinta el acceso en el sitio**, con la URL
 * intacta. Tres cosas salen gratis de esa decisión:
 *
 * - **No hay bucle posible**, porque no hay navegación.
 * - **No hace falta recordar a dónde iba**: sigue ahí. Quien abre el enlace de un
 *   recibo, se identifica y ve el recibo, no la lista de alumnos.
 * - Una pestaña que caduca mientras estaba abierta pide la contraseña donde estaba,
 *   en lugar de tirar al usuario al principio.
 */
import { useSesion } from '@/shared/auth/sesionContexto';
import { Acceso } from '@/acceso/Acceso';
import { Cargando } from '@/shared/ui/Estados';

export function ConSesion({ children }: { children: React.ReactNode }) {
  const { estado } = useSesion();

  /*
   * Hay un refresco en vuelo. Pintar el acceso aquí haría parpadear un formulario
   * de entrada a quien ya tiene sesión, en **cada** recarga.
   */
  if (estado.fase === 'arrancando') {
    return <Cargando filas={2} etiqueta="Comprobando la sesión" />;
  }

  if (estado.fase === 'fuera') return <Acceso />;

  return <>{children}</>;
}
